import { getNotAllowedParentGoalIds } from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useGoals, useSortedGoals, useUpdateGoal } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'shared/assets/icons';
import { GoalTreeItemIds } from 'shared/components/connected/goal-context-menu/types';
import {
  PopupMenu,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { ID } from 'shared/types/id';

import * as Styled from './move-goal-context-menu.style';
import { MoveGoalItem } from './move-goal-item';
import { useFilteredGoalTreeItems } from './use-filtered-goal-tree-items';

export type MoveGoalContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  id: ID;
  onClose: () => void;
  /**
   * When provided, the picker calls `onSelect` instead of mutating the goal
   * directly. Callers receive the resolved `parentIds` / `lifeAreaId` and own
   * the persistence (e.g. holding the value in form state until form Save).
   */
  onSelect?: (parentIds: ID[], lifeAreaId: ID | null) => void;
  /**
   * Section-move reuse: when set, the highlighted ("current") item is this
   * container — the goal or life area the section lives in — instead of the
   * `id` goal's parent. Only that container is disabled (descendants stay valid
   * targets, unlike goal-move cycle prevention).
   */
  sectionSource?: { goalId?: ID; lifeAreaId?: ID };
};

export const MoveGoalContextMenu: React.FC<MoveGoalContextMenuProps> = ({
  id,
  onClose,
  onSelect,
  sectionSource,
  position,
  location,
  referenceElement,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // need raw goals for recursive sub-goal lookups if filteredGoals contains items from useSortedGoals which might be filtered
  const allGoals = useGoals();
  // pass options to get all goals, as default is activeOnly
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goal = useMemo(() => goals.find((goal) => goal.id === id), [goals, id]);

  const [searchValue, setSearchValue] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<ID>>(() => {
    if (!goal) {
      return new Set();
    }
    const initial = new Set([goal.id, ...(goal.parentIds ?? [])]);

    // Resolve Life Area
    const goalMap = new Map<ID, (typeof allGoals)[0]>();
    allGoals.forEach((g) => goalMap.set(g.id, g));

    const resolveLifeAreaId = (gId: ID): ID | undefined => {
      const g = goalMap.get(gId);
      if (!g) {
        return undefined;
      }
      if (g.lifeAreaId) {
        return g.lifeAreaId;
      }
      if (!g.parentIds?.length) {
        return undefined;
      }
      // Recursively check parent
      return resolveLifeAreaId(g.parentIds[g.parentIds.length - 1]);
    };

    const laId = resolveLifeAreaId(goal.id);

    initial.add(laId ?? GoalTreeItemIds.NoLifeArea);

    return initial;
  });

  const { submit: updateGoal } = useUpdateGoal();

  useFocusChildrenWithArrows(containerRef);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const parentId = goal?.parentIds?.[goal.parentIds.length - 1];
  const currentLifeAreaId = goal?.lifeAreaId;
  const isSectionMove = !!sectionSource;

  const onGoal = (parentIds: ID[], lifeAreaId?: ID | null) => {
    if (onSelect) {
      onSelect(parentIds, lifeAreaId ?? null);
      onClose();
      return;
    }
    // If we move to a parent that is archived, we should also archive this goal
    const newParentId =
      parentIds.length > 0 ? parentIds[parentIds.length - 1] : undefined;
    const newParent = allGoals.find((g) => g.id === newParentId);
    const shouldArchive = !!newParent?.archivedAt && !goal?.archivedAt;

    updateGoal({
      id,
      parentIds,
      lifeAreaId,
      ...(shouldArchive ? { archivedAt: new Date() } : {}),
    });
    onClose();
  };

  const toggleCollapse = (e: React.MouseEvent, id: ID) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Create a map of goal indices for sorting
  const goalIndexMap = useMemo(() => {
    const map = new Map<ID, number>();
    goals.forEach((g, index) => map.set(g.id, index));
    return map;
  }, [goals]);

  const filteredItems = useFilteredGoalTreeItems({
    allGoals,
    lifeAreas,
    searchValue,
    expandedItems,
    goalIndexMap,
  });

  const notAllowedGoalIds = useMemo(
    () =>
      getNotAllowedParentGoalIds(id, {
        goals: allGoals,
        currentParentId: parentId,
      }),
    [id, parentId, allGoals],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Styled.Menu
      ref={containerRef}
      location={location}
      position={position}
      referenceElement={referenceElement}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            startIcon={Search}
            value={searchValue}
            placeholder={t('forms.goal.search.placeholder')}
            onChange={onChangeInput}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        {!filteredItems.length && !!searchValue && (
          <Styled.NoResultsContainer>
            <Typography variant="body2" color="text.secondary">
              {t('general.labels.no-results')}
            </Typography>
          </Styled.NoResultsContainer>
        )}
        {filteredItems.map((item) => {
          if (item.type === GoalTreeItemIds.NoLifeArea) {
            // A section can't live in "no life area" — never the section source.
            const isSelected =
              !isSectionMove && !parentId && !currentLifeAreaId && !searchValue;
            const isDisabled = isSelected;
            const isExpanded = expandedItems.has(GoalTreeItemIds.NoLifeArea);

            return (
              <MoveGoalItem
                key={GoalTreeItemIds.NoLifeArea}
                item={item}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isExpanded={isExpanded}
                onGoal={onGoal}
                toggleCollapse={toggleCollapse}
                searchValue={searchValue}
                isParent={false}
              />
            );
          }

          if (item.type === GoalTreeItemIds.LifeArea) {
            const isSelected = isSectionMove
              ? sectionSource.lifeAreaId === item.id && !searchValue
              : !parentId && currentLifeAreaId === item.id && !searchValue;

            // Disable the container the section currently lives in (or, for a
            // goal move, the goal's current life area).
            const isDisabled = isSelected;
            const isExpanded = expandedItems.has(item.id);

            return (
              <MoveGoalItem
                key={`life-area-${item.id}`}
                item={item}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isExpanded={isExpanded}
                onGoal={onGoal}
                toggleCollapse={toggleCollapse}
                searchValue={searchValue}
                isParent={false}
              />
            );
          }

          // Section move: highlight the source goal (the section's current
          // container) and disable only it — descendants stay valid targets.
          // Goal move: highlight the goal's parent and disable it + descendants.
          const isCurrentSectionGoal =
            isSectionMove && sectionSource.goalId === item.id;
          const isExpanded = expandedItems.has(item.id);
          const isParent = isSectionMove
            ? isCurrentSectionGoal
            : item.id === parentId;
          const isDisabled = isSectionMove
            ? isCurrentSectionGoal
            : notAllowedGoalIds.has(item.id);

          return (
            <MoveGoalItem
              key={`goal-${item.id}`}
              item={item}
              isSelected={isParent}
              isDisabled={isDisabled}
              isExpanded={isExpanded}
              onGoal={onGoal}
              toggleCollapse={toggleCollapse}
              searchValue={searchValue}
              isParent={isParent}
            />
          );
        })}
      </PopupMenuList>
    </Styled.Menu>
  );
};
